import { useMemo } from 'react'
import useWindowSize from './useWindowSize'

export enum MonitorResolution {
  _720P = '720P', // 1280 x 720
  HD = 'HD', // 1366 x 768
  FULL_HD = 'FULL_HD', // 1920 x 1080
  QHD = 'QHD', // 2560 x 1440
  UHD_4K = 'UHD_4K', // 3840 x 2160
  UW_QHD = 'UW_QHD', // 3440 x 1440
  UW_WQHD = 'UW_WQHD', // 3840 x 1600
  SUPER_UW = 'SUPER_UW', // 5120 x 1440
  SXGA = 'SXGA', // 1280 x 1024
  MBP_14_LARGE = 'MBP_14_LARGE', // 1800 x 1169
  MBP_14_DEFAULT = 'MBP_14_DEFAULT', // 1512 x 982
  MBP_14_MEDIUM = 'MBP_14_MEDIUM', // 1352 x 878
  MBP_14_SMALL = 'MBP_14_SMALL', // 1147 x 745
  MBP_14_SMALLEST = 'MBP_14_SMALLEST', // 1024 x 665
  UNKNOWN = 'UNKNOWN',
}

interface Resolution {
  width: number
  height: number
  threshold: number // Threshold percentage for matching
}

const RESOLUTIONS: Resolution[] = [
  { width: 1280, height: 720, threshold: 0.05 }, // 720p
  { width: 1366, height: 768, threshold: 0.05 }, // HD
  { width: 1920, height: 1080, threshold: 0.05 }, // Full HD
  { width: 2560, height: 1440, threshold: 0.05 }, // QHD
  { width: 3840, height: 2160, threshold: 0.05 }, // 4K UHD
  { width: 3440, height: 1440, threshold: 0.05 }, // UW-QHD
  { width: 3840, height: 1600, threshold: 0.05 }, // UW-WQHD
  { width: 5120, height: 1440, threshold: 0.05 }, // Super UW
  { width: 1280, height: 1024, threshold: 0.05 }, // SXGA
  { width: 1800, height: 1169, threshold: 0.05 }, // MBP 14" Large
  { width: 1512, height: 982, threshold: 0.05 }, // MBP 14" Default
  { width: 1352, height: 878, threshold: 0.05 }, // MBP 14" Medium
  { width: 1147, height: 745, threshold: 0.05 }, // MBP 14" Small
  { width: 1024, height: 665, threshold: 0.05 }, // MBP 14" Smallest
]

const useMonitorResolution = (): MonitorResolution => {
  const { width, height } = useWindowSize()

  return useMemo(() => {
    // Find the closest matching resolution
    let closestMatch = {
      resolution: MonitorResolution.UNKNOWN,
      difference: Number.MAX_VALUE,
    }

    RESOLUTIONS.forEach((resolution, index) => {
      const widthDiff = Math.abs(width - resolution.width) / resolution.width
      const heightDiff =
        Math.abs(height - resolution.height) / resolution.height
      const totalDiff = widthDiff + heightDiff

      if (
        totalDiff < closestMatch.difference &&
        totalDiff <= resolution.threshold * 2
      ) {
        closestMatch.difference = totalDiff
        switch (index) {
          case 0:
            closestMatch.resolution = MonitorResolution._720P
            break
          case 1:
            closestMatch.resolution = MonitorResolution.HD
            break
          case 2:
            closestMatch.resolution = MonitorResolution.FULL_HD
            break
          case 3:
            closestMatch.resolution = MonitorResolution.QHD
            break
          case 4:
            closestMatch.resolution = MonitorResolution.UHD_4K
            break
          case 5:
            closestMatch.resolution = MonitorResolution.UW_QHD
            break
          case 6:
            closestMatch.resolution = MonitorResolution.UW_WQHD
            break
          case 7:
            closestMatch.resolution = MonitorResolution.SUPER_UW
            break
          case 8:
            closestMatch.resolution = MonitorResolution.SXGA
            break
          case 9:
            closestMatch.resolution = MonitorResolution.MBP_14_LARGE
            break
          case 10:
            closestMatch.resolution = MonitorResolution.MBP_14_DEFAULT
            break
          case 11:
            closestMatch.resolution = MonitorResolution.MBP_14_MEDIUM
            break
          case 12:
            closestMatch.resolution = MonitorResolution.MBP_14_SMALL
            break
          case 13:
            closestMatch.resolution = MonitorResolution.MBP_14_SMALLEST
            break
        }
      }
    })

    return closestMatch.resolution
  }, [width, height])
}

export default useMonitorResolution
